import { type Callback, type Dict } from "mixpanel-browser";
import type Mixpanel from "mixpanel-browser";

import { type SegmentEvents, segmentTrack } from "./segment";

declare global {
  interface Window {
    mixpanel?: typeof Mixpanel;
    commonMixPanelProps?: Dict;
  }
}

// Global variable to store common mixpanel properties
let commonMixPanelProps: Dict = {};

// Function to set common properties that will be included in all tracking calls
export const setCommonMixPanelProps = (props: Dict) => {
  commonMixPanelProps = props;
  globalThis.commonMixPanelProps = props;
};

const MixpanelToken =
  process.env.WP_CALC === "true"
    ? process.env.REACT_APP_MIXPANEL_CALC_TOKEN
    : process.env.REACT_APP_MIXPANEL_TOKEN || "";

// we enable mixpanel on:
// 1. not cypress testing
// 2. we have token to mixpanel AND
// 3. REACT_APP_ENABLE_MIXPANEL is true (explicit enable mixpanel) OR
// 4. we are in production

const shouldInitMixPanel =
  process.env.CYPRESS_INTEGRATION_MODE !== "true" &&
  MixpanelToken &&
  (process.env.REACT_APP_ENABLE_MIXPANEL === "true" || process.env.NODE_ENV === "production");

// all mixpanel method we use in CMP, if you need a new one add that here and to the stub
export interface MinimumMixpanel {
  track(event_name: string, properties?: Dict): void;
  identify(unique_id?: string): any;
  register(props: Dict, days?: number): void;
  people: {
    set(prop: string, to: any, callback?: Callback): void;
    set(props: Record<string, any>): void;
  };
}

// Create our custom track function that will replace Mixpanel's track
const customTrackFunction = (event_name: string, properties?: Dict) => {
  // Merge common properties with event-specific properties
  const mergedProperties = { ...commonMixPanelProps, ...properties };

  if (globalThis.segmentTrackEventCallback) {
    globalThis.segmentTrackEventCallback(event_name, mergedProperties);
  } else {
    segmentTrack(event_name as unknown as SegmentEvents, mergedProperties);
  }
};

if (globalThis.mixpanel && shouldInitMixPanel) {
  globalThis.mixpanel.init(MixpanelToken, {
    debug: false,
    record_sessions_percent: parseInt(process.env.REACT_APP_MIXPANEL_REPLAYS_SESSION_SAMPLE_PERCENT || "0"),
  });

  // After initialization, replace the track function with our custom implementation
  // Use type assertion to ensure compatibility with Mixpanel's track method
  globalThis.mixpanel.track = customTrackFunction as typeof globalThis.mixpanel.track;
}

// // can't use noop as those needs to be new  instances of functions
const mixpanel: MinimumMixpanel = (shouldInitMixPanel && globalThis.mixpanel) || {
  track: customTrackFunction as MinimumMixpanel["track"],
  identify: new Function() as MinimumMixpanel["identify"],
  register: new Function() as MinimumMixpanel["register"],
  people: {
    set: new Function() as MinimumMixpanel["people"]["set"],
  },
};

export const mixpanelTrack = (event: string, properties?: Dict) => {
  mixpanel.track(event, properties);
};

export default mixpanel;
